import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getFullscreenPortalTarget } from './fullscreenPortal';
import { FaCalendarPlus, FaTimes, FaClock, FaDoorOpen, FaLock, FaSpinner } from 'react-icons/fa';
import API from '../../API';
import { useOverlay } from '../../contexts/FocusContext';

export interface BookRoomModalRoom {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
}

export interface BookRoomModalProps {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  eventId: string;
  rooms: BookRoomModalRoom[];
  onBookingCreated?: () => void;
  isStaff?: boolean;
}

const MAIN_ROOM_ID = 'intersect-main';

const getUtcDateValue = (date: Date) => date.toISOString().slice(0, 10);

const getNextUtcMinuteValue = (date: Date) => {
  const nextMinute = new Date(date);
  nextMinute.setUTCSeconds(0, 0);
  nextMinute.setUTCMinutes(nextMinute.getUTCMinutes() + 1);
  return nextMinute.toISOString().slice(11, 16);
};

const isPastBookingTime = (date: string, time: string, currentUtc: Date) => {
  if (!date || !time) return false;
  const startDateTime = new Date(`${date}T${time}:00Z`);
  return Number.isNaN(startDateTime.getTime()) || startDateTime <= currentUtc;
};

const setUnityKeyboardCapture = (capture: boolean) => {
  if ((window as any).sendMessageToUnity) {
    try {
      const captureKeyboardData = {
        eventType: 'CaptureKeyboard',
        payload: JSON.stringify({ capture }),
      };
      (window as any).sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
    } catch (error) {
      console.error('[BookRoomModal] Error setting Unity capture:', error);
    }
  }
};

const blurUnityCanvas = () => {
  const canvas = document.querySelector('#unity-canvas') as HTMLCanvasElement;
  if (canvas) {
    canvas.blur();
    canvas.setAttribute('tabindex', '-1');
  }
  const container = document.querySelector('#unity-container') as HTMLElement;
  if (container) {
    container.blur();
  }
};

const restoreUnityCanvas = () => {
  const canvas = document.querySelector('#unity-canvas') as HTMLCanvasElement;
  if (canvas) {
    canvas.removeAttribute('tabindex');
  }
};

export const BookRoomModal: React.FC<BookRoomModalProps> = ({
  open,
  onClose,
  accessToken,
  eventId,
  rooms,
  onBookingCreated,
  isStaff = false,
}) => {
  const availableRooms = React.useMemo(() => {
    return isStaff ? rooms : rooms.filter((room) => room.room_id !== MAIN_ROOM_ID);
  }, [rooms, isStaff]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successPrivateCode, setSuccessPrivateCode] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useOverlay('book-room-form', open && isInputFocused);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
    setUnityKeyboardCapture(false);
    blurUnityCanvas();
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
    setTimeout(() => {
      if (
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA' &&
        document.activeElement?.tagName !== 'SELECT'
      ) {
        // Unity capture restored when modal closes
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (!open) return;

    const stopKeyboardForUnity = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isFormElement =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      if (isFormElement) {
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', stopKeyboardForUnity, true);
    window.addEventListener('keyup', stopKeyboardForUnity, true);
    window.addEventListener('keypress', stopKeyboardForUnity, true);

    return () => {
      window.removeEventListener('keydown', stopKeyboardForUnity, true);
      window.removeEventListener('keyup', stopKeyboardForUnity, true);
      window.removeEventListener('keypress', stopKeyboardForUnity, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      blurUnityCanvas();
      setUnityKeyboardCapture(false);
      const focusTimer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(focusTimer);
    }
    restoreUnityCanvas();
    setUnityKeyboardCapture(true);
  }, [open]);

  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [currentUtc, setCurrentUtc] = useState(new Date());

  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      setCurrentUtc(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelectedRoomId(availableRooms.length > 0 ? availableRooms[0].room_id : '');
      setTitle('');
      setDescription('');
      setError(null);
      setSuccess(false);
      setSuccessPrivateCode(null);
      setIsPrivate(false);
      const now = new Date();
      const nextHourUtc = new Date(now.getTime() + 60 * 60 * 1000);
      setDate(getUtcDateValue(nextHourUtc));
      const utcHours = nextHourUtc.getUTCHours();
      setTime(`${utcHours.toString().padStart(2, '0')}:00`);
    }
    wasOpenRef.current = open;
  }, [open, availableRooms]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isPastBookingTime(date, time, new Date())) {
      setError('Please choose a booking time in the future.');
      return;
    }

    setIsSubmitting(true);

    try {
      const startDateTime = new Date(`${date}T${time}:00Z`);
      const bookingData = {
        eventId,
        room_id: selectedRoomId,
        title: title.trim(),
        description: description.trim(),
        start_time: startDateTime.toISOString(),
        duration_minutes: duration,
        is_private: isPrivate,
      };

      const response = await API.createBooking(accessToken, bookingData);

      if (response.status === 201) {
        const data = await response.json();
        setSuccess(true);
        const privateCode =
          data.booking?.is_private && data.booking?.access_code ? data.booking.access_code : null;
        if (privateCode) {
          setSuccessPrivateCode(privateCode);
        }
        const closeDelay = privateCode ? 5000 : 1500;
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setSuccessPrivateCode(null);
          onBookingCreated?.();
        }, closeDelay);
      } else if (response.status === 409) {
        const errorData = await response.text();
        setError(`Time slot conflict: ${errorData}`);
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to create booking');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error creating booking:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestClose = () => {
    if (!isSubmitting) onClose();
  };

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const currentUtcDate = getUtcDateValue(currentUtc);
  const selectedTimeIsPast = isPastBookingTime(date, time, currentUtc);
  const minimumTime = date === currentUtcDate ? getNextUtcMinuteValue(currentUtc) : undefined;

  return createPortal(
    <div
      className="unity-custom-cursor fixed inset-0 z-[10052] flex items-center justify-center bg-black bg-opacity-75"
      onClick={handleRequestClose}
    >
      <div
        className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-[#333] w-[90%] max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-[#333]">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FaCalendarPlus className="text-green-500" />
            Book a Room
          </h2>
          <button
            type="button"
            onClick={handleRequestClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-[#2a2a2a] rounded"
            disabled={isSubmitting}
          >
            <FaTimes size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {success && (
            <div className="p-4 bg-green-900/30 border border-green-500/50 rounded-lg text-green-300 text-center space-y-2">
              <p>Booking created successfully!</p>
              {successPrivateCode && (
                <div className="pt-2">
                  <p className="text-sm text-gray-300 mb-1">Share this code for others to join:</p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <span className="font-mono text-lg font-bold text-white bg-[#2a2a2a] px-3 py-1 rounded">
                      {successPrivateCode}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(successPrivateCode);
                      }}
                      className="px-3 py-1 bg-[#2a2a2a] hover:bg-[#333] text-white rounded text-sm border border-[#444]"
                    >
                      Copy code
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <FaDoorOpen className="inline mr-2" />
              Select Room
            </label>
            <select
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none"
              required
              disabled={isSubmitting}
            >
              {availableRooms.map((room) => (
                <option key={room.room_id} value={room.room_id}>
                  {room.room_id === MAIN_ROOM_ID ? `❗ ${room.room_name} (Staff Only)` : room.room_name}
                </option>
              ))}
            </select>
            {isStaff && selectedRoomId === MAIN_ROOM_ID && (
              <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                ❗ You have staff privileges to book the main room
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Enter booking title"
              className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
              required
              maxLength={100}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Enter booking description"
              className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none resize-none"
              rows={2}
              maxLength={500}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="book-room-private"
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              className="w-4 h-4 rounded border-[#444] bg-[#2a2a2a] text-green-600 focus:ring-green-500"
              disabled={isSubmitting}
            />
            <label htmlFor="book-room-private" className="text-sm text-gray-300 flex items-center gap-1 cursor-pointer">
              <FaLock className="text-green-500" size={14} />
              Private room (share a code for others to join)
            </label>
          </div>

          <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400 mb-1">Current UTC Time</div>
            <div className="text-lg font-mono text-green-400">
              {(currentUtc.getUTCMonth() + 1).toString().padStart(2, '0')}/
              {currentUtc.getUTCDate().toString().padStart(2, '0')}/{currentUtc.getUTCFullYear()}{' '}
              {currentUtc.toISOString().slice(11, 19)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Date (UTC)</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={currentUtcDate}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Time (UTC, 24h)</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                min={minimumTime}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                step="60"
                className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                required
                disabled={isSubmitting}
              />
              <div className="text-xs text-gray-500 mt-1">e.g. 14:00 = 2:00 PM</div>
            </div>
          </div>

          {selectedTimeIsPast && (
            <p className="text-sm text-gray-400">
              Past times are unavailable. Choose a future UTC time.
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <FaClock className="inline mr-2" />
              Duration
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10))}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none"
              disabled={isSubmitting}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !title.trim() || !selectedRoomId || selectedTimeIsPast}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaCalendarPlus />
                  Create Booking
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">All times are in UTC (Coordinated Universal Time)</p>
        </form>
      </div>
    </div>,
    getFullscreenPortalTarget()
  );
};
